import { Directive, ElementRef, OnInit, Renderer2, Input } from '@angular/core';

@Directive({
  selector: '[eventPropagationFix]'
})
export class EventPropagationFixDirective implements OnInit {
  @Input() customZIndex?: number;
  @Input() disableZIndexManagement: boolean = false;

  private contextCache: string | null = null;

  constructor(private el: ElementRef, private renderer: Renderer2) {}

  ngOnInit() {
    const element = this.el.nativeElement;
    const tagName = element.tagName.toLowerCase();
    const inputType = element.type ? element.type.toLowerCase() : '';

    // For radio buttons and checkboxes, don't stop propagation - let them work naturally
    if (tagName === 'input' && (inputType === 'radio' || inputType === 'checkbox')) {
      // Don't add any event listeners that stop propagation for radio buttons and checkboxes
      // Just apply the styling fixes
      this.applyElementSpecificFixes(element, tagName, inputType);
      return;
    }

    // Stop click propagation for other elements, but not for anchor tags
    if (tagName !== 'a') {
      this.renderer.listen(element, 'click', (event: Event) => {
        event.stopPropagation();
      });
    }
    
    if (tagName === 'input' && inputType === 'image') {
      this.renderer.listen(element, 'mousedown', (event: Event) => {
        event.stopPropagation();
      });
    }
    
    // Apply enhanced element-specific fixes with smart z-index
    this.applyElementSpecificFixes(element, tagName, inputType);
  }

  private applyElementSpecificFixes(element: HTMLElement, tagName: string, inputType?: string) {
    this.renderer.setStyle(element, 'position', 'relative');
    this.renderer.setStyle(element, 'pointer-events', 'auto');

    // Apply smart z-index management unless disabled
    if (!this.disableZIndexManagement) {
      const zIndex = this.calculateSmartZIndex(element, tagName, inputType);
      this.renderer.setStyle(element, 'z-index', zIndex.toString());
    }

    // Apply additional styling for specific elements
    if (tagName === 'a') {
      this.renderer.setStyle(element, 'display', 'inline-block');
    }

    // Enhanced fixes for radio buttons and checkboxes
    if (tagName === 'input' && (inputType === 'radio' || inputType === 'checkbox')) {
      if (inputType === 'radio') {
        // For radio buttons, always enhance the label to make it clickable
        // This works regardless of whether the radio button is hidden or visible
        this.enhanceRadioButtonLabel(element);
      } else if (inputType === 'checkbox') {
        // For checkboxes, apply standard fixes
        this.renderer.setStyle(element, 'pointer-events', 'auto', 1);
        this.renderer.setStyle(element, 'position', 'relative', 1);
        
        // Ensure minimum clickable area for checkboxes
        this.renderer.setStyle(element, 'min-width', '16px');
        this.renderer.setStyle(element, 'min-height', '16px');
      }
      
      // Handle parent container if it has specific classes
      const parentElement = element.parentElement;
      if (parentElement) {
        if (parentElement.classList.contains('form-check') || 
            parentElement.classList.contains('checkBoxContent') ||
            parentElement.classList.contains('form-group')) {
          this.renderer.setStyle(parentElement, 'position', 'relative');
          this.renderer.setStyle(parentElement, 'z-index', 'auto');
        }
      }
    }
  }

  private calculateSmartZIndex(element: HTMLElement, tagName: string, inputType?: string): number {
    // Use custom z-index if provided
    if (this.customZIndex !== undefined) {
      return this.customZIndex;
    }

    // Detect context and apply appropriate z-index
    const context = this.detectContext(element);
    const baseZIndex = this.getBaseZIndexForElement(tagName, inputType);

    switch (context) {
      case 'modal':
        return baseZIndex + 1050; // Bootstrap modal z-index is 1050
      case 'overlay':
        return baseZIndex + 1999; // High priority overlays
      case 'dropdown':
        return baseZIndex + 1000; // Bootstrap dropdown z-index is 1000
      case 'tooltip':
        return baseZIndex + 1070; // Bootstrap tooltip z-index is 1070
      case 'popover':
        return baseZIndex + 1060; // Bootstrap popover z-index is 1060
      default:
        return baseZIndex;
    }
  }

  private getBaseZIndexForElement(tagName: string, inputType?: string): number {
    switch (tagName) {
      case 'input':
        if (inputType === 'checkbox' || inputType === 'radio' || inputType === 'file' || inputType==='image') {
          return 10; // Higher base for interactive elements
        }
        return 5;
      case 'mat-checkbox':
      case 'mat-slider':
      case 'mat-select':
        return 10; // Material components need slightly higher base
      case 'a':
        return 2;
      default:
        return 1;
    }
  }

  private detectContext(element: HTMLElement): string {
    // Use cached result if available
    if (this.contextCache !== null) {
      return this.contextCache;
    }

    let currentElement: HTMLElement | null = element.parentElement;
    
    while (currentElement) {
      const classList = currentElement.classList;
      const tagName = currentElement.tagName.toLowerCase();

      // Check for modal contexts
      if (classList.contains('modal') || 
          classList.contains('mat-dialog-container') ||
          classList.contains('cdk-overlay-container') ||
          tagName === 'mat-dialog-container') {
        this.contextCache = 'modal';
        return 'modal';
      }

      // Check for overlay contexts
      if (classList.contains('cdk-overlay-pane') ||
          classList.contains('mat-menu-panel') ||
          classList.contains('overlay') ||
          classList.contains('mat-autocomplete-panel')) {
        this.contextCache = 'overlay';
        return 'overlay';
      }

      // Check for dropdown contexts
      if (classList.contains('dropdown-menu') ||
          classList.contains('mat-select-panel') ||
          classList.contains('dropdown') ||
          tagName === 'mat-option') {
        this.contextCache = 'dropdown';
        return 'dropdown';
      }

      // Check for tooltip contexts
      if (classList.contains('tooltip') ||
          classList.contains('mat-tooltip') ||
          classList.contains('mat-tooltip-panel')) {
        this.contextCache = 'tooltip';
        return 'tooltip';
      }

      // Check for popover contexts
      if (classList.contains('popover') ||
          classList.contains('mat-menu-content')) {
        this.contextCache = 'popover';
        return 'popover';
      }

      currentElement = currentElement.parentElement;
    }

    this.contextCache = 'normal';
    return 'normal';
  }

  private enhanceRadioButtonLabel(element: HTMLElement) {
    // Find the associated label for the radio button
    const elementId = element.id;
    let label: HTMLElement | null = null;
    
    if (elementId) {
      // Look for label with 'for' attribute matching the input id
      label = document.querySelector(`label[for="${elementId}"]`) as HTMLElement;
    }
    
    if (!label) {
      // Look for parent label element
      label = element.closest('label') as HTMLElement;
    }
    
    if (!label) {
      // Look for sibling label
      const nextSibling = element.nextElementSibling;
      if (nextSibling && nextSibling.tagName.toLowerCase() === 'label') {
        label = nextSibling as HTMLElement;
      }
    }
    
    if (label) {
      // Make sure the label is clickable and has proper styling
      this.renderer.setStyle(label, 'cursor', 'pointer');
      this.renderer.setStyle(label, 'pointer-events', 'auto');
      this.renderer.setStyle(label, 'position', 'relative');
      this.renderer.setStyle(label, 'z-index', '10');
      
      // Remove any existing click listeners to avoid duplicates
      // Add click listener to the label that will properly trigger the radio button
      this.renderer.listen(label, 'click', (event: Event) => {
        event.preventDefault();
        event.stopPropagation();
        
        // Manually trigger the radio button
        const inputElement = element as HTMLInputElement;
        if (!inputElement.checked) {
          inputElement.checked = true;
          // Dispatch change event to trigger Angular's change detection
          const changeEvent = new Event('change', { bubbles: true });
          inputElement.dispatchEvent(changeEvent);
        }
      });
    }
    
    // Also ensure the radio button itself can be clicked if it becomes visible
    this.renderer.setStyle(element, 'pointer-events', 'auto');
    this.renderer.setStyle(element, 'z-index', '5');
  }

  private createClickableOverlay(element: HTMLElement) {
    // For hidden radio buttons, we need to make the label clickable
    // Find the associated label
    const elementId = element.id;
    let label: HTMLElement | null = null;
    
    if (elementId) {
      // Look for label with 'for' attribute matching the input id
      label = document.querySelector(`label[for="${elementId}"]`) as HTMLElement;
    }
    
    if (!label) {
      // Look for parent label element
      label = element.closest('label') as HTMLElement;
    }
    
    if (!label) {
      // Look for sibling label
      const nextSibling = element.nextElementSibling;
      if (nextSibling && nextSibling.tagName.toLowerCase() === 'label') {
        label = nextSibling as HTMLElement;
      }
    }
    
    if (label) {
      // Make sure the label is clickable and has proper styling
      this.renderer.setStyle(label, 'cursor', 'pointer');
      this.renderer.setStyle(label, 'pointer-events', 'auto');
      this.renderer.setStyle(label, 'position', 'relative');
      this.renderer.setStyle(label, 'z-index', '10');
      
      // Add click listener to the label that will trigger the hidden input
      this.renderer.listen(label, 'click', (event: Event) => {
        event.stopPropagation();
        // Trigger the hidden input
        element.click();
      });
    } else {
      // If no label found, make the hidden input itself clickable by expanding its clickable area
      this.renderer.setStyle(element, 'position', 'absolute');
      this.renderer.setStyle(element, 'width', '100%');
      this.renderer.setStyle(element, 'height', '100%');
      this.renderer.setStyle(element, 'opacity', '0');
      this.renderer.setStyle(element, 'cursor', 'pointer');
      this.renderer.setStyle(element, 'z-index', '10');
      this.renderer.setStyle(element, 'pointer-events', 'auto');
    }
  }
}
